import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { SiteFooter } from "@/components/SiteFooter";
import { PageGallery } from "@/components/PageGallery";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import type { ConsularFaqItem } from "@/content/consular-corpus/faq";
import { CONSULAR_FAQ_CATEGORIES, getConsularCategoryDef } from "@/content/consular-corpus/faq-defs";

export const Route = createFileRoute("/consular-legalization/faq/$category")({
  loader: async ({ params }) => {
    const [{ getConsularFaqsByCategoryTh }, { CONSULAR_KEYWORDS_TH_TOP }] = await Promise.all([
      import("@/content/consular-corpus/categories"),
      import("@/content/consular-corpus/top.gen"),
    ]);
    const def = getConsularCategoryDef(params.category);
    if (!def) throw notFound();
    return {
      def,
      faqs: getConsularFaqsByCategoryTh(def.slug),
      keywords: CONSULAR_KEYWORDS_TH_TOP.slice(0, 60),
    };
  },
  component: ConsularFaqCategoryPage,
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "หมวดคำถามไม่พบ" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { def, faqs, keywords } = loaderData;
    return {
      ...pageHeadForProvince({
        title: `${def.titleTh} — รับรองกงสุล FAQ ${faqs.length} ข้อ (2026)`,
        description: def.descTh,
        path: `/consular-legalization/faq/${params.category}`,
        type: "article",
        alternates: [{ lang: "en", path: `/en/consular-legalization/faq/${params.category}` }],
        keywords,
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "รับรองกงสุล FAQ", url: `${site}/consular-legalization/faq` },
              { name: def.titleTh, url: `${site}/consular-legalization/faq/${params.category}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/consular-legalization/faq/${params.category}`)) },
      ],
    };
  },
});

function ConsularFaqCategoryPage() {
  const { def, faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <a href="/consular-legalization/faq" className="hover:underline">รับรองกงสุล FAQ</a> <span>/</span>{" "}
          <span>{def.titleTh}</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">{def.titleTh}</h1>
        <p className="text-muted-foreground mb-6">{def.descTh}</p>
        <p className="text-sm text-muted-foreground mb-8">
          รวม <strong>{faqs.length}</strong> คำถามในหมวดนี้ — ตอบโดยทีมทนายผู้รับมอบอำนาจ อัปเดต 2026
        </p>

        <div className="mb-8 flex flex-wrap gap-2 text-xs">
          {CONSULAR_FAQ_CATEGORIES.map((c) => (
            <a
              key={c.slug}
              href={`/consular-legalization/faq/${c.slug}`}
              className={`rounded-full border px-3 py-1 ${c.slug === def.slug ? "bg-primary text-primary-foreground" : "bg-muted/40 text-muted-foreground hover:bg-muted"}`}
            >
              {c.titleTh}
            </a>
          ))}
        </div>

        <div className="space-y-4">
          {faqs.map((item: ConsularFaqItem, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="text-muted-foreground mr-2">Q{i + 1}.</span>{item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
          {faqs.length === 0 && (
            <p className="rounded-lg border bg-muted/30 p-6 text-sm text-muted-foreground">
              ยังไม่มีคำถามในหมวดนี้ — <a href="/contact" className="text-primary underline">สอบถามทีมงาน</a>
            </p>
          )}
        </div>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">บริการรับรองกงสุลครบวงจร</h2>
          <p className="text-sm text-muted-foreground mb-4">
            แปล → นิติกรณ์ MFA → สถานทูตปลายทาง / Apostille 2026 → จัดส่งทั่วโลก
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">
            ติดต่อทีมงาน →
          </a>
        </section>
      
        <FaqSourceNote topic="consular" locale="th" />
        <div className="mb-10">
          <PageGallery category="consular" seed="consular-faq.category" locale="th" />
        </div>

      </main>
      <SiteFooter />
    </>
  );
}